/**
 * selfServiceTotals.ts — Agregación de los montos de una MESA de autoservicio, COMPARTIDA por
 * los motores de impresión (IR webble, ticket Electron y PDF pdfMake).
 *
 * Una mesa se cobra y se imprime como UN solo ticket: los N pedidos que la componen se suman
 * campo por campo para armar el pedido sintético. `propina` y `costo_envio` NO se sumaban —
 * salían del spread del PRIMER pedido— así que la propina de los pedidos 2..N nunca llegaba
 * al papel. Medido en producción (90 días): 1,765 mesas con más de un pedido, 24 de ellas con
 * propina en 2 o más pedidos y ~$778 de propina perdida. Es dinero del mesero.
 *
 * Coerción por término (TKCN-543/542): el BE puede mandar estos campos como string y el `+`
 * CONCATENARÍA en vez de sumar; `toFiniteAmount` además absorbe los nulos/ausentes que
 * `Number()` convertía en NaN. `roundToCents` cierra la deriva de punto flotante ANTES de que
 * el monto quede guardado en el pedido sintético (PRJ-010: lo calculado se redondea, no solo
 * se formatea).
 *
 * NO toca la firma de dedup de auto-impresión (`printJobSignature`): esa firma se arma con la
 * configuración de la impresora, nunca con los montos del pedido.
 */

import { Order, OrderDetail } from '@interfaces/OrderTypes';
import { roundToCents, toFiniteAmount } from '@utils/formats';

// Los cinco campos de dinero que el pedido sintético de mesa sobrescribe.
export type SelfServiceAmounts = Pick<Order, 'subtotal' | 'total' | 'coupon_amount' | 'propina' | 'costo_envio'>;

type MoneyField = keyof SelfServiceAmounts;

// Suma un campo de dinero a lo largo de los pedidos de la mesa, redondeada a centavos.
const sumField = (orders: OrderDetail[], field: MoneyField): number =>
  roundToCents(orders.reduce((acc, order) => acc + toFiniteAmount(order?.pedido?.[field]), 0));

/**
 * Suma los montos de los pedidos de una mesa para armar el pedido sintético que se imprime.
 *
 * @param {OrderDetail[]} orders Pedidos de la mesa (ya ordenados; el orden no afecta la suma).
 * @returns {SelfServiceAmounts} Los cinco campos de dinero agregados y redondeados a centavos.
 */
export const sumSelfServiceAmounts = (orders: OrderDetail[]): SelfServiceAmounts => ({
  subtotal: sumField(orders, 'subtotal'),
  total: sumField(orders, 'total'),
  coupon_amount: sumField(orders, 'coupon_amount'),
  propina: sumField(orders, 'propina'),
  costo_envio: sumField(orders, 'costo_envio'),
});
